import { Box, Typography } from '@mui/material';
import { differenceInMinutes } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useChildTokens } from './useChildTokens';
import { NextActivityResult } from './activityStatus';

/**
 * TransitionWarning — tyst, visuell förvarning inför en kommande aktivitet
 * (Pipeline 3, övergångsförvarning). Visas BARA när nästa aktivitet är 'upcoming'
 * och startar inom 0–10 minuter. Ingen ljud/pling/animation: NPF-vänligt och
 * lågmält — bara en lugn bärnstensbanner som signalerar "snart är det dags".
 *
 * Datakällan är medvetet via prop: ChildHome beräknar nextUp EN gång (getNextActivity)
 * och skickar resultatet + renderTime hit. Det undviker dubbel beräkning och håller
 * komponenten ren/testbar (inga context-beroenden).
 */
const TransitionWarning = ({
  result,
  renderTime,
}: {
  result: NextActivityResult;
  renderTime: Date;
}) => {
  const { t } = useTranslation();
  const tokens = useChildTokens();

  // Endast kommande aktiviteter förvarnas (en pågående är redan igång).
  if (result.status !== 'upcoming') return null;

  const start = new Date(result.instance.startDate);
  if (Number.isNaN(start.getTime())) return null;

  const minsUntil = differenceInMinutes(start, renderTime);
  if (minsUntil < 0 || minsUntil > 10) return null;

  const name = result.instance.activityName;
  const message =
    minsUntil === 0
      ? t('child.transition.warningNow', { name })
      : t('child.transition.warning', { name, mins: minsUntil });

  return (
    <Box
      role="status"
      sx={{
        bgcolor: tokens.amberLight,
        color: tokens.attentionMain,
        border: '1px solid',
        borderColor: tokens.attentionMain,
        borderRadius: '14px',
        p: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
      }}
    >
      <Box aria-hidden sx={{ fontSize: '1.25rem', lineHeight: 1, flexShrink: 0 }}>
        ⏳
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: 'inherit' }}>
        {message}
      </Typography>
    </Box>
  );
};

export default TransitionWarning;
